/**
 * The one function the rest of the app calls to log a product event.
 *
 *   import { track } from '@/lib/analytics/track';
 *   import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
 *   track(ANALYTICS_EVENTS.JOB_CREATED, { job_type: 'boiler_service' });
 *
 * Guard-rails baked in so call sites never have to think about them:
 *   - no-ops if the SDK isn't ready (no key / SSR / opted out)
 *   - never throws (analytics must not break a user flow)
 *   - only accepts names from the typed taxonomy
 */
import * as amplitude from '@amplitude/analytics-browser';
import { isAmplitudeReady } from './amplitude';
import { forwardToAppsFlyer } from '@/lib/native/appsflyer';
import type { AnalyticsEventName, EventProperties } from './events';

export function track(event: AnalyticsEventName, properties?: EventProperties): void {
  // Native-only ad-attribution forward (no-op on web). Outside the Amplitude
  // readiness gate: an opted-out Amplitude user is still an attributable
  // install, and the bridge has its own guards.
  try {
    forwardToAppsFlyer(event);
  } catch {
    // Swallow — attribution must never break a user flow.
  }
  if (!isAmplitudeReady()) return;
  try {
    amplitude.track(event, properties);
  } catch {
    // Swallow — a failed analytics call must never surface to the user.
  }
}

/**
 * Force-flush queued events. Amplitude batches on a timer, so the LAST event
 * before a HARD navigation (window.location, not router.push) can be lost.
 * Call this after firing such an event — e.g. ONBOARDING_COMPLETED right
 * before the redirect out of onboarding. Best-effort; never throws.
 */
export function flushAnalytics(): void {
  if (!isAmplitudeReady()) return;
  try {
    amplitude.flush();
  } catch {
    // Swallow — flushing is best-effort.
  }
}
